import { PromiseCompletionSource } from "async/PromiseCompletionSource";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { BaseScalableView } from "by/gamefactory/cityversetycoon/scenes/common/BaseScalableView";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { IFocusable } from "by/gamefactory/cityversetycoon/scenes/layers/IFocusable";
import { ShowRemoveSceneParams } from "../ShowRemoveSceneParams";
import { IRemoveBuildingController } from "../controllers/IRemoveBuildingController";
import { CompletionPage } from "./pages/CompletionPage";
import { ConfirmationPage } from "./pages/ConfirmationPage";
import { CancellationToken } from "async/CancellationToken";
import { IDestroyOptions } from "pixi.js";
import { StarsResourcePackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/StarsResourcePackage";

export class RemoveBuildingPopupView extends BaseScalableView<boolean> implements IFocusable {
    public readonly focusable: boolean = true;

    private readonly cancellationToken: CancellationToken;
    private readonly commonResources: CommonResourcesPackage;
    private readonly starsResources: StarsResourcePackage;
    private readonly removeBuildingController: IRemoveBuildingController;
    private readonly showRemoveSceneParams: ShowRemoveSceneParams;

    public constructor(
        operationControls: PromiseCompletionSource<boolean>,
        commonResources: CommonResourcesPackage,
        starsResources: StarsResourcePackage,
        canvasScaler: ICanvasScaler,
        removeBuildingController: IRemoveBuildingController,
        showRemoveSceneParams: ShowRemoveSceneParams
    ) {
        super(operationControls, canvasScaler);
        this.cancellationToken = showRemoveSceneParams.cancellationToken;
        this.commonResources = commonResources;
        this.starsResources = starsResources;
        this.removeBuildingController = removeBuildingController;
        this.showRemoveSceneParams = showRemoveSceneParams;
        this.cancellationToken.onCanceled.add(this.onCancellationRequested, this);
    }

    public async start(): Promise<void> {
        const mainPageCS = new PromiseCompletionSource<boolean>();
        const mainPage = new ConfirmationPage(
            mainPageCS,
            this.canvasScaler,
            this.commonResources,
            this.starsResources,
            this.removeBuildingController,
            this.showRemoveSceneParams
        );
        this.addChild(mainPage);
        const result = await mainPageCS.result;
        if (result) {
            this.removeChild(mainPage);
            const secondaryPageCS = new PromiseCompletionSource<void>();
            const secondaryPage = new CompletionPage(
                secondaryPageCS,
                this.canvasScaler,
                this.commonResources,
                this.starsResources
            );
            this.addChild(secondaryPage);
            await secondaryPageCS.result;
            this.removeChild(secondaryPage);
        }
        this.operationControls.complete(result);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.cancellationToken.onCanceled.remove(this.onCancellationRequested, this);
        super.destroy(options);
    }

    private onCancellationRequested(): void {
        this.operationControls.complete(false);
        this.destroy();
    }
}
